import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import {
  FaCarAlt,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUserAlt,
  FaCheck,
  FaTimes,
  FaSearch,
  FaBuilding,
} from "react-icons/fa";
import { API_URL } from "../../config/apiConfig";
import AdminLayout from "../../layouts/AdminLayout";
import {
  getPendingBookings,
  reset,
} from "../../features/admin/adminSlice";

const BookingAssignment = () => {
  const dispatch = useDispatch();
  const {
    pendingBookings,
    loading,
    error,
    success,
    message,
  } = useSelector((state) => state.admin);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendors, setVendors] = useState([]);

  useEffect(() => {
    dispatch(getPendingBookings());
    fetchVendors();

    // Set up a polling interval to refresh data
    const interval = setInterval(() => {
      dispatch(getPendingBookings());
    }, 30000); // Every 30 seconds

    return () => {
      clearInterval(interval);
      dispatch(reset());
    };
  }, [dispatch]);

  // Fetch vendors
  const fetchVendors = async () => {
    try {
      // Get token from stored user object
      const storedUser = JSON.parse(localStorage.getItem('user'));
      const token = storedUser?.token || storedUser?.data?.token;
      console.log('Fetching vendors with token:', token ? 'present' : 'missing');
      console.log('Stored user structure:', storedUser);

      const response = await axios.get(`${API_URL}/admin/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Vendors API response:', response.data);

      if (response.data && response.data.status && response.data.data && response.data.data.vendors) {
        console.log('Setting vendors from data.vendors:', response.data.data.vendors);
        setVendors(response.data.data.vendors);
      } else if (response.data && response.data.vendors) {
        console.log('Setting vendors from direct vendors field:', response.data.vendors);
        setVendors(response.data.vendors);
      } else {
        console.log('No vendors found in response:', response.data);
        setVendors([]);
      }
    } catch (error) {
      console.error('Error fetching vendors:');
      console.error('Full error object:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error message:', error.message);
      setVendors([]);
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(reset());
    }

    if (success) {
      toast.success(message);
      setSelectedBooking(null);
      setSelectedVendor(null);
      dispatch(reset());
    }
  }, [error, success, message, dispatch]);

  useEffect(() => {
    if (pendingBookings) {
      filterBookings();
    }
  }, [pendingBookings, searchTerm]);

  const filterBookings = () => {
    if (!pendingBookings) {
      return;
    }

    if (!searchTerm) {
      setFilteredBookings(pendingBookings);
    } else {
      const filtered = pendingBookings.filter(
        (booking) =>
          (booking.bookingId &&
            booking.bookingId
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          // Check for user fields from MongoDB data
          (booking.user?.name &&
            booking.user.name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (booking.user?.phone && booking.user.phone.includes(searchTerm)) ||
          (booking.user?.email &&
            booking.user.email
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          // Check legacy passenger fields
          (booking.passengerDetails?.name &&
            booking.passengerDetails.name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (booking.passengerDetails?.phone &&
            booking.passengerDetails.phone.includes(searchTerm)) ||
          // Check location fields
          (booking.pickupLocation?.name &&
            booking.pickupLocation.name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())) ||
          (booking.dropLocation?.name &&
            booking.dropLocation.name
              .toLowerCase()
              .includes(searchTerm.toLowerCase())),
      );
      setFilteredBookings(filtered);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectBooking = (booking) => {
    setSelectedBooking(booking);
    setSelectedVendor(null);
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v._id === vendorId);
    setSelectedVendor(vendor);
  };

  const handleAssignToVendor = async () => {
    if (selectedBooking && selectedVendor) {
      const vendorName = selectedVendor.fullName || selectedVendor.name || 'Unknown Vendor';
      const confirmAssignment = `Assign booking to vendor?\n\n` +
        `Vendor: ${vendorName}\n` +
        `Booking ID: ${selectedBooking.bookingId}\n` +
        `The vendor will assign one of their drivers through their dashboard.`;

      if (!window.confirm(confirmAssignment)) {
        return;
      }

      try {
        // Get token from stored user object
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const token = storedUser?.token || storedUser?.data?.token;
        const response = await axios.post(
          `${API_URL}/booking/${selectedBooking._id}/assign-vendor`,
          { vendorId: selectedVendor._id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.status) {
          toast.success(
            `✅ Booking assigned to vendor successfully!\n` +
            `🏢 ${vendorName} will now assign a driver through their dashboard\n` +
            `📱 The vendor has been notified of this assignment`,
            { duration: 6000 }
          );

          // Reset selections and refresh bookings
          setSelectedBooking(null);
          setSelectedVendor(null);
          dispatch(getPendingBookings());
        }
      } catch (error) {
        console.error('Error assigning booking to vendor:', error);
        toast.error(
          error.response?.data?.message || 
          'Failed to assign booking to vendor. Please try again.'
        );
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading && !pendingBookings) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">Vendor Assignment</h1>
          <p className="text-gray-600">Assign bookings to vendors - vendors will then assign their drivers</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Bookings List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="text-lg font-semibold">Pending Bookings</h2>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search bookings..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                </div>
              </div>

              {filteredBookings && filteredBookings.length > 0 ? (
                <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
                  {filteredBookings.map((booking) => (
                    <div
                      key={booking._id}
                      onClick={() => handleSelectBooking(booking)}
                      className={`p-4 border-b last:border-b-0 hover:bg-gray-50 cursor-pointer transition duration-150 ${selectedBooking?._id === booking._id ? "bg-blue-50 border-l-4 border-blue-500" : ""}`}
                    >
                      <div className="flex justify-between mb-2">
                        <div className="font-medium text-gray-900">
                          {booking.bookingId}
                        </div>
                        <div className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                          {String(booking.status || "pending")}
                        </div>
                      </div>
                      <div className="flex items-center text-sm mb-2">
                        <FaUserAlt className="text-gray-400 mr-2" />
                        <span>
                          {booking.user?.name ||
                            booking.passengerDetails?.name ||
                            "No name"}
                          {booking.user?.phone && ` • ${booking.user.phone}`}
                          {!booking.user?.phone &&
                            booking.passengerDetails?.phone &&
                            ` • ${booking.passengerDetails.phone}`}
                        </span>
                      </div>
                      <div className="flex items-center text-sm mb-2">
                        <FaMapMarkerAlt className="text-gray-400 mr-2" />
                        <span>
                          {booking.pickupLocation?.name || "Unknown"}
                          {booking.pickupLocation?.state &&
                            `, ${booking.pickupLocation.state}`}
                        </span>
                      </div>
                      <div className="flex items-center text-sm mb-2">
                        <FaMapMarkerAlt className="text-red-400 mr-2" />
                        <span>
                          {booking.dropLocation?.name || "Unknown"}
                          {booking.dropLocation?.state &&
                            `, ${booking.dropLocation.state}`}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <FaCalendarAlt className="mr-2" />
                        <span>
                          {formatDate(booking.pickupDate)}, {booking.pickupTime}
                          {booking.cabType?.name &&
                            ` • ${booking.cabType.name}`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <FaCarAlt className="text-gray-400 text-xl" />
                  </div>
                  <h3 className="text-lg font-medium mb-1">
                    No Pending Bookings
                  </h3>
                  <p className="text-gray-500">
                    {searchTerm
                      ? "No bookings match your search criteria."
                      : "There are no bookings pending assignment."}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Assignment Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Vendor Assignment</h2>
              </div>

              {selectedBooking ? (
                <div className="p-4">
                  <div className="mb-4">
                    <h3 className="text-md font-medium mb-2">
                      Booking Details
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <p className="text-xs text-gray-500">Booking ID</p>
                          <p className="text-sm font-medium">
                            {selectedBooking.bookingId}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Status</p>
                          <p className="text-sm">
                            <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                              {String(selectedBooking.status || "pending")}
                            </span>
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Passenger</p>
                          <p className="text-sm">
                            {selectedBooking.user?.name ||
                              selectedBooking.passengerDetails?.name ||
                              ""}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="text-sm">
                            {selectedBooking.user?.phone ||
                              selectedBooking.passengerDetails?.phone ||
                              ""}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Pickup</p>
                          <p className="text-sm">
                            {selectedBooking.pickupLocation?.name || ""}
                            {selectedBooking.pickupLocation?.state &&
                              `, ${selectedBooking.pickupLocation.state}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Drop</p>
                          <p className="text-sm">
                            {selectedBooking.dropLocation?.name || ""}
                            {selectedBooking.dropLocation?.state &&
                              `, ${selectedBooking.dropLocation.state}`}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Date & Time</p>
                          <p className="text-sm">
                            {formatDate(selectedBooking.pickupDate)},{" "}
                            {selectedBooking.pickupTime}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Cab Type</p>
                          <p className="text-sm">
                            {selectedBooking.cabType?.name || "Standard"}
                          </p>
                        </div>
                        {selectedBooking.distance && (
                          <div>
                            <p className="text-xs text-gray-500">Distance</p>
                            <p className="text-sm">
                              {selectedBooking.distance} km
                            </p>
                          </div>
                        )}
                        {selectedBooking.totalAmount && (
                          <div>
                            <p className="text-xs text-gray-500">
                              Total Amount
                            </p>
                            <p className="text-sm">
                              ₹{selectedBooking.totalAmount}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Vendor Selection */}
                  <div className="mb-4">
                    <h3 className="text-md font-medium mb-2">
                      Select Vendor
                    </h3>
                    <div className="mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Choose Vendor
                      </label>
                      <select
                        value={selectedVendor?._id || ""}
                        onChange={(e) => handleVendorChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Choose a vendor...</option>
                        {vendors.map((vendor) => (
                          <option key={vendor._id} value={vendor._id}>
                            {vendor.fullName || vendor.name}
                            {vendor.verificationStatus && ` (${vendor.verificationStatus})`}
                            {vendor.walletInfo && ` - ₹${vendor.walletInfo.totalBalance.toFixed(2)} wallet, ${vendor.walletInfo.totalDrivers} drivers`}
                          </option>
                        ))}
                      </select>
                    </div>
                    {selectedVendor && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <h4 className="text-sm font-medium text-blue-800 mb-2">Selected Vendor</h4>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Name:</span>
                            <span className="font-medium">{selectedVendor.fullName || selectedVendor.name}</span>
                          </div>
                          {selectedVendor.email && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Email:</span>
                              <span>{selectedVendor.email}</span>
                            </div>
                          )}
                          {selectedVendor.phone && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Phone:</span>
                              <span>{selectedVendor.phone}</span>
                            </div>
                          )}
                          {selectedVendor.verificationStatus && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Status:</span>
                              <span className={`px-2 py-1 rounded text-xs ${
                                selectedVendor.verificationStatus === 'approved'
                                  ? 'bg-green-100 text-green-800'
                                  : selectedVendor.verificationStatus === 'pending'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {selectedVendor.verificationStatus}
                              </span>
                            </div>
                          )}
                          {selectedVendor.walletInfo && (
                            <>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Drivers:</span>
                                <span>{selectedVendor.walletInfo.totalDrivers} ({selectedVendor.walletInfo.activeDrivers} active)</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Total Earnings:</span>
                                <span className="font-medium text-green-600">
                                  ₹{Number((selectedVendor.walletInfo.totalEarnings ?? selectedVendor.walletInfo.totalCommissionEarnings ?? selectedVendor.walletInfo.totalBalance ?? 0)).toFixed(2)}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedVendor && (
                    <div className="mb-4">
                      <h3 className="text-md font-medium mb-2">
                        Assignment Information
                      </h3>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <FaBuilding className="h-5 w-5 text-yellow-400" />
                          </div>
                          <div className="ml-3">
                            <h4 className="text-sm font-medium text-yellow-800">
                              Vendor Driver Assignment
                            </h4>
                            <div className="mt-2 text-sm text-yellow-700">
                              <p>• The booking will be assigned to <strong>{selectedVendor.fullName || selectedVendor.name}</strong></p>
                              <p>�� The vendor will receive a notification to assign one of their drivers</p>
                              <p>• The vendor will manage driver assignment through their vendor dashboard</p>
                              <p>• You can track the assignment status in the bookings list</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-3">
                    <button
                      onClick={handleAssignToVendor}
                      disabled={!selectedVendor || loading}
                      className={`flex-1 flex flex-col items-center justify-center py-3 px-4 rounded-lg ${
                        selectedVendor && !loading
                          ? "bg-primary hover:bg-primary-dark text-white"
                          : "bg-gray-200 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      <div className="flex items-center">
                        <FaBuilding className="mr-2" />
                        Assign to Vendor
                      </div>
                      {selectedVendor && selectedBooking && (
                        <span className="text-xs mt-1 opacity-90">
                          Vendor will assign their own driver
                        </span>
                      )}
                    </button>

                    <button
                      onClick={() => {
                        setSelectedBooking(null);
                        setSelectedVendor(null);
                      }}
                      className="flex items-center justify-center bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg"
                    >
                      <FaTimes className="mr-2" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-3">
                    <FaCarAlt className="text-gray-400 text-xl" />
                  </div>
                  <h3 className="text-lg font-medium mb-1">
                    No Booking Selected
                  </h3>
                  <p className="text-gray-500">
                    Select a booking from the list to assign to a vendor
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default BookingAssignment;
